import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import apiManager from '@/api/AxiosInstance';
import newApiManager from '@/api/NewAxiosInstance';
import DualFooterButton from '@/components/Common/Button/DualFooterButton';
import AlertModal from '@/components/Common/Modal/AlertModal';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import BasicTabs from '@/components/Common/Tabs/BasicTabs';
import MenuHeader from '@/components/Header/MenuHeader';
import ConfirmExitPopup from '@/components/Products/ConfirmExitPopup';
import CarInfo from '@/components/Products/Sale/DetailTab/CarInfo';
import DetailInfo from '@/components/Products/Sale/DetailTab/DetailInfo';
import EvidentiaryDocuments from '@/components/Products/Sale/DetailTab/EvidentiaryDocuments';
import SalePrice from '@/components/Products/Sale/DetailTab/SalePrice';
import VehicleImageUploader from '@/components/Products/Sale/VehicleImageUploader';
import { colors } from '@/const/colors';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { BEFORE_SALE, PAUSE, SALE } from '@/const/productStatus';
import { HASH_DETAIL, HASH_DOCUMENT, HASH_PHOTO, HASH_PRICE, HASH_PRODUCT_INFO } from '@/const/products';
import { phoneNumberPatternRegex } from '@/const/regex';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { useMemberContext } from '@/contexts/Member/MemberContext';
import useBlockNavigation from '@/hooks/useBlockNavigation';
import useExitConfirmationPopup from '@/hooks/useExitConfirmationPopup';
import useNavigateBack from '@/hooks/useNavigateBack';
import DefaultHelmet from '@/metadatas/DefaultHelmet';
import NotFoundPage from '@/pages/Error/NotFoundPage';
import { isUnderFourTons, requiresSaleAuthorityDocuments } from '@/utils/common';
import { canModifyProduct } from '@/utils/Products/productUtils';

const MyCarDetailForm: React.FC = () => {
  const navigate = useNavigate();
  const navigateBack = useNavigateBack();
  const location = useLocation();
  const { showToast } = useToastContext();
  const [renderCount, setRenderCount] = useState(0);
  const productStatusRef = useRef<string>();
  const isClicked = useRef<boolean>();
  const initialProductInfoRef = useRef<ProductDetailResponse | null>(null);
  const [isDisabledSaveBtn, setIsDisabledSaveBtn] = useState(false);
  const { member } = useMemberContext();

  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const [isConfirmModalConfig, setIsConfirmModalConfig] = useState<ModalConfig>({
    title: '',
    content: null,
    leftButtonLabel: '',
    rightButtonLabel: '',
    onLeftButtonClick: () => {},
    onRightButtonClick: () => {},
  });

  const isDealerType = member?.type ? member.type?.code !== 'NORMAL' : false;

  /** 초기 데이터와 비교하여 실제 변경사항이 있는지 확인 (딜러 승인 대기 방지) */
  const buildProductModifyRequest = (
    data: ProductDetailResponse,
    status: string,
  ): Omit<ProductModifyRequest, 'status'> => {
    const transformedCarOptionData = {
      normalOption: {
        etc: data?.carOption?.normalOption?.etc || '',
        option: data?.carOption?.normalOption?.option?.map((item) => item.code) || [],
      },
      additionalOption: {
        etc: data?.carOption?.additionalOption?.etc || '',
        option: data?.carOption?.additionalOption?.option?.map((item) => item.code) || [],
      },
      breakOption: {
        etc: data?.carOption?.breakOption?.etc || '',
        option: data?.carOption?.breakOption?.option?.map((item) => item.code) || [],
      },
    };
    const transformedMaintenanceData = {
      maintenanceCategories: data?.maintenance?.maintenanceData?.map((item) => item.code) || [],
      etc: data?.maintenance?.etc || '',
    };
    const isTonsOne = Number(data?.tons) === 1 || Number(data?.tons) === 1.1 || Number(data?.tons) === 1.2;
    const getAreaValue = (area: any): string => {
      if (typeof area === 'string' && area.trim()) return area;
      return area?.desc && typeof area.desc === 'string' && area.desc.trim() ? area.desc : '';
    };
    const area1Value = isTonsOne ? getAreaValue(data?.area1) : '';
    const area2Value = isTonsOne ? getAreaValue(data?.area2) : '';
    const area3Value = isTonsOne ? getAreaValue(data?.area3) : '';

    return {
      accident: data?.accidentsHistory?.accident || false,
      accidentContents: data?.accidentsHistory?.accident
        ? data?.accidentsHistory?.accidentContents || ''
        : '',
      axis: isUnderFourTons(data?.tons) ? 'NONE' : data?.axis?.code,
      backImageUrl: data?.productsImage?.backImageUrl || '',
      backSideImageUrl: data?.productsImage?.backSideImageUrl || '',
      carOption: transformedCarOptionData,
      certificateImageUrl: data?.productsImage?.certificateImageUrl || '',
      color: data?.color?.code,
      dashboardImageUrl: data?.productsImage?.dashboardImageUrl || '',
      detailContent: data?.detailContent || '',
      distance: data?.distance,
      engineImageUrl: data?.productsImage?.engineImageUrl || '',
      engineVideoUrl: data?.productsImage?.engineVideoUrl || '',
      frontImageUrl: data?.productsImage?.frontImageUrl || '',
      frontSideImageUrl: data?.productsImage?.frontSideImageUrl || '',
      fuel: data?.fuel?.code,
      garage: data?.garage?.code,
      insideImageUrl: data?.productsImage?.insideImageUrl || '',
      loaded: data?.loaded?.code,
      // 선택하지 않으면 서버에 남아있을 수 있어 빈 값으로 초기화 의도를 전달
      loadedDetail: data?.loadedDetail?.code ?? '',
      loadedInnerArea: data?.loadedInnerArea,
      loadedInnerHeight: data?.loadedInnerHeight,
      loadedInnerLength: data?.loadedInnerLength,
      maintenanceData: transformedMaintenanceData,
      modelId: data?.model?.id,
      modelDetail: data?.modelDetail?.code ?? '',
      optionImageUrls: data?.productsImage?.optionImageUrl || null,
      power: data?.power || '',
      price: data?.price,
      productsType: productType?.code,
      sheetImageUrl: data?.productsImage?.sheetImageUrl || '',
      tireImageUrl: data?.productsImage?.tireImageUrl || '',
      tireStatus: data?.tireStatus?.code,
      tons: data?.tons,
      transmission: data?.transmission?.code,
      transportEndLocate: data?.transportEndLocate?.code,
      transportGoods: data?.transportGoods,
      transportStartLocate: data?.transportStartLocate?.code,
      area1: area1Value,
      area2: area2Value,
      area3: area3Value,
      evidentiaryDocumentsUrl1: data?.productsImage?.evidentiaryDocumentsUrl1 || '',
      evidentiaryDocumentsUrl2: data?.productsImage?.evidentiaryDocumentsUrl2 || '',
      evidentiaryDocumentsUrl3: data?.productsImage?.evidentiaryDocumentsUrl3 || '',
      evidentiaryDocumentsUrl4: data?.productsImage?.evidentiaryDocumentsUrl4 || '',
      evidentiaryDocumentsUrl5: data?.productsImage?.evidentiaryDocumentsUrl5 || '',
    };
  };

  const hasProductChanged = (status: string): boolean => {
    const initial = initialProductInfoRef.current;
    if (!initial) return true; // 초기 데이터가 없으면 변경된 것으로 간주 (첫 로드 직후)
    const currentData = buildProductModifyRequest(productInfo, status);
    const initialData = buildProductModifyRequest(initial, status);
    return JSON.stringify(currentData) !== JSON.stringify(initialData);
  };

  const { isShowExitConfirmPopup, openExitConfirmPopup, closeExitConfirmPopup, confirmExit } = useExitConfirmationPopup(
    () => false,
    () => {},
    () => {},
  );

  useBlockNavigation(openExitConfirmPopup);

  const [isEtcManufacturerCategories, setIsEtcManufacturerCategories] = useState<boolean>();

  const [productInfo, setProductInfo] = useState<ProductDetailResponse>({
    productStatus: { code: '', desc: '' },
    id: 0,
    status: { code: '', desc: '' },
    manufacturerCategories: { id: 0, name: '', code: '' },
    model: { id: 0, name: '' },
    tons: '',
    color: { code: '', desc: '' },
    loaded: { code: '', desc: '' },
    loadedInnerArea: '',
    loadedInnerLength: '',
    loadedInnerHeight: '',
    axis: { code: '', desc: '' },
    transmission: { code: '', desc: '' },
    distance: 0,
    fuel: { code: 'DIESEL', desc: '디젤' },
    power: '',
    garage: { code: '', desc: '' },
    productsImage: {
      id: 0,
      certificateImageUrl: '',
      frontImageUrl: '',
      frontSideImageUrl: '',
      backImageUrl: '',
      backSideImageUrl: '',
      tireImageUrl: '',
      engineImageUrl: '',
      insideImageUrl: '',
      dashboardImageUrl: '',
      sheetImageUrl: '',
      engineVideoUrl: '',
      optionImageUrl: null,
    },
    accidentsHistory: {
      accident: false,
      accidentContents: '',
    },
    maintenance: { maintenanceData: [{ code: '', desc: '' }], etc: '' },
    tireStatus: null,
    transportStartLocate: { code: '', desc: '' },
    transportEndLocate: { code: '', desc: '' },
    transportGoods: '',
    carOption: {
      normalOption: { etc: '', option: [{ code: '', desc: '' }] },
      additionalOption: { etc: '', option: [{ code: '', desc: '' }] },
      breakOption: { etc: '', option: [{ code: '', desc: '' }] },
    },
    detailContent: '',
    price: null,
    actualSalePrice: null,
    sellerId: 0,
    sellerSafetyNumber: '',
  });
  const { id } = useParams();

  const getTabNum = () => {
    const hash = window.location.hash.substring(1);
    if (hash === HASH_PRODUCT_INFO) {
      return 0;
    } else if (hash === HASH_DETAIL) {
      return 1;
    } else if (hash === HASH_PHOTO) {
      return 2;
    } else if (hash === HASH_PRICE) {
      // 딜러는 판매가격 탭이 없어 사진수정(2)으로 보정한다.
      return isDealerType ? 2 : 3;
    } else if (hash === HASH_DOCUMENT) {
      // 증빙서류는 항상 마지막 탭 (딜러 3, 일반 4)
      return isDealerType ? 3 : 4;
    } else {
      return 0;
    }
  };

  const [tabNum, setTabNum] = useState(getTabNum());
  const [isShow, setIsShow] = useState(false);
  const [productType, setProductType] = useState<EnumPresenter>();
  // 회원 실명이 소유주명과 다르고 현물출자(위수탁) 이력에도 없으면(판매 권한 미확인) 증빙서류 탭을 노출한다.
  const showDocumentTab = requiresSaleAuthorityDocuments(
    member?.realName,
    productInfo?.ownerName,
    productInfo?.lastOwnerInfo?.content,
  );
  const baseLabelList: TabType[] = isDealerType
    ? [
        { icon: null, title: '차량정보' },
        { icon: null, title: '상세정보' },
        { icon: null, title: '사진수정' },
      ]
    : [
        { icon: null, title: '차량정보' },
        { icon: null, title: '상세정보' },
        { icon: null, title: '사진수정' },
        { icon: null, title: '판매가격' },
      ];
  const labelList: TabType[] = showDocumentTab
    ? [...baseLabelList, { icon: null, title: '증빙서류' }]
    : baseLabelList;
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);
  const isTabNumFirst = tabNum === 0;

  const buttonList: Button[] = [
    {
      title: '이전',
      bgColor: colors.gray[0],
      borderColor: colors.gray[3],
      textColor: colors.gray[6],
      isDisabled: isTabNumFirst,
      onClick: () => {
        isTabNumFirst ? '' : onChangeTab(tabNum - 1);
      },
    },
  ];

  const lastTabIndex = labelList.length - 1;
  const isLastTab = tabNum === lastTabIndex;

  const nextPrice = Number(productInfo?.price);
  const originalPrice = Number(initialProductInfoRef.current?.price);
  if (isLastTab) {
    // 마지막 탭일 때 '저장하기' 버튼 추가 (딜러+tabNum 3인 경우도 보정)
    buttonList.push({
      title: '저장하기',
      bgColor: '',
      borderColor: 'transparent',
      isDisabled: false,
      onClick: () => {
        // handleRegistrationComplete(productInfo?.status.code, true);
        if (nextPrice <= originalPrice - 50) {
          setIsConfirmModal(true);
          setIsConfirmModalConfig({
            title: '차량 판매 금액 수정 완료',
            content: (
              <>
                <div className="mb-4">
                  50만원 이상 수정되어 <br />
                  해당 차량이 상단에 노출돼요.
                </div>
              </>
            ),
            leftButtonLabel: '닫기',
            rightButtonLabel: '확인',
            onLeftButtonClick: () => {
              setIsConfirmModal(false);
            },
            onRightButtonClick: () => {
              setIsConfirmModal(false);
              handleRegistrationComplete(productInfo?.status.code, true);
            },
          });
        } else {
          handleRegistrationComplete(productInfo?.status.code, true);
        }
      },
    });
  } else {
    const nextTab = labelList[tabNum + 1];
    if (nextTab) {
      buttonList.push({
        title: `다음(${nextTab.title})`,
        bgColor: '',
        borderColor: 'transparent',
        isDisabled: false,
        onClick: () => onChangeTab(tabNum + 1),
      });
    }
  }

  const isValidateChecked = () => {
    const {
      model,
      tons,
      color,
      loaded,
      // loadedInnerArea,
      loadedInnerLength,
      // loadedInnerHeight,
      axis,
      transmission,
      distance,
      fuel,
      power,
      garage,
      productsImage,
      detailContent,
      price,
    } = productInfo;

    const valuesToCheckCarInfo = [model, tons, loaded, loadedInnerLength, axis, distance, fuel, transmission, power];

    const valuesToCheckImage = [
      productsImage,
      productsImage?.frontSideImageUrl,
      productsImage?.backSideImageUrl,
      productsImage?.frontImageUrl,
    ];

    if (tabNum === 0 && valuesToCheckCarInfo.some((value) => value == null || value === '' || value === 0)) {
      showToast('필수값을 모두 입력해주세요.', 'error', 'bottom');
      isClicked.current = false;
      return false;
    } else if (tabNum === 2 && valuesToCheckImage.some((value) => value == null || value === '')) {
      showToast('필수값을 모두 입력해주세요.', 'error', 'bottom');
      isClicked.current = false;
      return false;
    } else if (
      tabNum === 1 &&
      productInfo?.accidentsHistory?.accident === true &&
      !String(productInfo?.accidentsHistory?.accidentContents ?? '').trim()
    ) {
      showToast('필수값을 모두 입력해주세요.', 'error', 'bottom');
      isClicked.current = false;
      return false;
    } else if (tabNum === 1 && detailContent && isValidPhoneNumber(detailContent)) {
      setIsShow(true);

      isClicked.current = false;
      return false;
    } else if (!isDealerType && tabNum === 3 && (price === null || price === 0 || price === undefined)) {
      showToast('필수값을 모두 입력해주세요.', 'error', 'bottom');
      isClicked.current = false;
      return false;
    } else {
      return true;
    }
  };

  const handleRegistrationComplete = (status: string, isRegisterBtn: boolean) => {
    if (isClicked.current) {
      return;
    }

    // 실제 변경사항이 없으면 PATCH 호출하지 않음 (딜러 승인 대기 방지)
    if (!hasProductChanged(status)) {
      if (isRegisterBtn) {
        showToast('변경된 내용이 없습니다.', 'success', 'bottom');
        navigate(`/products/${id}`, { replace: true });
      }
      return;
    }

    const transformedCarOptionData = {
      normalOption: {
        etc: productInfo?.carOption?.normalOption?.etc || '',
        option: productInfo?.carOption?.normalOption?.option.map((item) => item.code) || [],
      },
      additionalOption: {
        etc: productInfo?.carOption?.additionalOption?.etc || '',
        option: productInfo?.carOption?.additionalOption?.option.map((item) => item.code) || [],
      },
      breakOption: {
        etc: productInfo?.carOption?.breakOption?.etc || '',
        option: productInfo?.carOption?.breakOption?.option.map((item) => item.code) || [],
      },
    };

    const transformedMaintenanceData = {
      maintenanceCategories: productInfo?.maintenance?.maintenanceData?.map((item) => item.code) || [],
      etc: productInfo?.maintenance?.etc || '',
    };

    const isTonsOne =
      Number(productInfo?.tons) === 1 || Number(productInfo?.tons) === 1.1 || Number(productInfo?.tons) === 1.2;
    const getAreaValue = (area: any): string => {
      if (typeof area === 'string' && area.trim()) return area;
      return area?.desc && typeof area.desc === 'string' && area.desc.trim() ? area.desc : '';
    };

    const area1Value = isTonsOne ? getAreaValue(productInfo?.area1) : '';
    const area2Value = isTonsOne ? getAreaValue(productInfo?.area2) : '';
    const area3Value = isTonsOne ? getAreaValue(productInfo?.area3) : '';

    const requestData: ProductModifyRequest = {
      accident: productInfo?.accidentsHistory?.accident || false,
      accidentContents: productInfo?.accidentsHistory?.accident
        ? productInfo?.accidentsHistory?.accidentContents || ''
        : '',
      axis: isUnderFourTons(productInfo?.tons) ? 'NONE' : productInfo?.axis?.code,
      backImageUrl: productInfo?.productsImage?.backImageUrl || '',
      backSideImageUrl: productInfo?.productsImage?.backSideImageUrl || '',
      carOption: transformedCarOptionData,
      certificateImageUrl: productInfo?.productsImage?.certificateImageUrl || '',
      color: productInfo?.color?.code,
      dashboardImageUrl: productInfo?.productsImage?.dashboardImageUrl || '',
      detailContent: productInfo?.detailContent || '',
      distance: productInfo?.distance,
      engineImageUrl: productInfo?.productsImage?.engineImageUrl || '',
      engineVideoUrl: productInfo?.productsImage?.engineVideoUrl || '',
      frontImageUrl: productInfo?.productsImage?.frontImageUrl || '',
      frontSideImageUrl: productInfo?.productsImage?.frontSideImageUrl || '',
      fuel: productInfo?.fuel?.code,
      garage: productInfo?.garage?.code,
      insideImageUrl: productInfo?.productsImage?.insideImageUrl || '',
      loaded: productInfo?.loaded?.code,
      // loaded 변경 후 세부 적재함을 선택하지 않으면, 서버에 남아있던 이전 값이 유지될 수 있어 빈 값으로 초기화
      loadedDetail: productInfo?.loadedDetail?.code ?? '',
      loadedInnerArea: productInfo?.loadedInnerArea,
      loadedInnerHeight: productInfo?.loadedInnerHeight,
      loadedInnerLength: productInfo?.loadedInnerLength,
      maintenanceData: transformedMaintenanceData,
      modelId: productInfo?.model?.id,
      // model 변경 후 세부 모델을 선택하지 않으면 동일 문제 발생 가능
      modelDetail: productInfo?.modelDetail?.code ?? '',
      optionImageUrls: productInfo?.productsImage?.optionImageUrl || null,
      power: productInfo?.power || '',
      price: productInfo?.price,
      status: status,
      productsType: productType?.code,
      sheetImageUrl: productInfo?.productsImage?.sheetImageUrl || '',
      tireImageUrl: productInfo?.productsImage?.tireImageUrl || '',
      tireStatus: productInfo?.tireStatus?.code,
      tons: productInfo?.tons,
      transmission: productInfo?.transmission?.code,
      transportEndLocate: productInfo?.transportEndLocate?.code,
      transportGoods: productInfo?.transportGoods,
      transportStartLocate: productInfo?.transportStartLocate?.code,
      area1: area1Value,
      area2: area2Value,
      area3: area3Value,
      evidentiaryDocumentsUrl1: productInfo?.productsImage?.evidentiaryDocumentsUrl1,
      evidentiaryDocumentsUrl2: productInfo?.productsImage?.evidentiaryDocumentsUrl2,
      evidentiaryDocumentsUrl3: productInfo?.productsImage?.evidentiaryDocumentsUrl3,
      evidentiaryDocumentsUrl4: productInfo?.productsImage?.evidentiaryDocumentsUrl4,
      evidentiaryDocumentsUrl5: productInfo?.productsImage?.evidentiaryDocumentsUrl5,
    };

    isClicked.current = true;

    if (isRegisterBtn || status === SALE) {
      if (isValidateChecked()) {
        patchProducts(requestData, isRegisterBtn);
      }
    } else {
      patchProducts(requestData, isRegisterBtn);
    }
  };

  const isValidPhoneNumber = (content: string | undefined) => {
    if (!content) {
      return;
    }
    return phoneNumberPatternRegex.test(content);
  };

  const patchProducts = (requestData: ProductModifyRequest, isRegisterBtn: boolean) => {
    newApiManager
      .patch(`/api/v1/products/${id}`, requestData)
      .then((response) => {
        const serverResponseData = response.data;

        const responseTons = serverResponseData?.tons;
        const isResponseTonsOne =
          responseTons != null
            ? Number(responseTons) === 1 || Number(responseTons) === 1.1 || Number(responseTons) === 1.2
            : false;

        if (!isResponseTonsOne) {
          serverResponseData.area1 = '';
          serverResponseData.area2 = '';
          serverResponseData.area3 = '';
        }

        initialProductInfoRef.current = JSON.parse(JSON.stringify(serverResponseData));
        setProductInfo(serverResponseData);
        if (isRegisterBtn) {
          navigate(`/products/${id}`, {
            state: { isRegisterBtn: productStatusRef.current === BEFORE_SALE },
            replace: true,
          });
        }
      })
      .catch((error) => {
        if (error.code === 'THREE_IMAGE_LEAST') {
          showToast(error.message, 'error', 'bottom');
          setTabNum(2);
        } else if (error.code === 'MISSING_REQUEST_PARAMETER') {
          showToast('필수값을 모두 입력해주세요.', 'error', 'bottom');
          setTabNum(0);
        } else {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        }
      })
      .finally(() => {
        isClicked.current = false;
      });
  };

  const onChangeTab = (newTabNum: number): boolean => {
    if (isValidateChecked()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTabNum(newTabNum);
      let newHash = '';
      switch (newTabNum) {
        case 0:
          newHash = HASH_PRODUCT_INFO;
          break;
        case 1:
          newHash = HASH_DETAIL;
          break;
        case 2:
          newHash = HASH_PHOTO;
          break;
        case 3:
          // 딜러는 판매가격 탭이 없어 3번이 증빙서류다.
          newHash = isDealerType ? HASH_DOCUMENT : HASH_PRICE;
          break;
        case 4:
          newHash = HASH_DOCUMENT;
          break;
      }
      navigate(`#${newHash}`, { replace: true });
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (location.state) {
      setProductType(location.state.buttonKey);
    }
  }, [location.state]);

  useEffect(() => {
    if (!location.state) {
      newApiManager
        .get('/api/v1/products/' + id)
        .then((response) => {
          const serverResponseData = response.data;

          if (!canModifyProduct(serverResponseData?.type?.code)) {
            showToast('매입 견적 상품은 수정할 수 없어요.', 'error', 'bottom');
            navigate('/products/my', { replace: true });
            return;
          }

          // 초기 데이터에서도 tons가 1이 아니면 활동지 데이터를 ''로 설정
          const initialTons = serverResponseData?.tons;
          const isInitialTonsOne =
            initialTons != null
              ? Number(initialTons) === 1 || Number(initialTons) === 1.1 || Number(initialTons) === 1.2
              : false;

          if (!isInitialTonsOne) {
            serverResponseData.area1 = '';
            serverResponseData.area2 = '';
            serverResponseData.area3 = '';
          }

          initialProductInfoRef.current = JSON.parse(JSON.stringify(serverResponseData));
          setProductInfo(serverResponseData);
          setIsEtcManufacturerCategories(serverResponseData?.manufacturerCategories?.code === 'ETC');
          handleHashChange();
        })
        .catch(() => {
          setIsShowErrorPage(true);
        });
    } else {
      const stateData = location.state;

      if (!canModifyProduct(stateData?.type?.code)) {
        showToast('매입 견적 상품은 수정할 수 없어요.', 'error', 'bottom');
        navigate('/products/my', { replace: true });
        return;
      }

      // location.state에서도 tons가 1이 아니면 활동지 데이터를 ''로 설정
      const stateTons = stateData?.tons;
      const isStateTonsOne =
        stateTons != null ? Number(stateTons) === 1 || Number(stateTons) === 1.1 || Number(stateTons) === 1.2 : false;

      if (!isStateTonsOne) {
        stateData.area1 = '';
        stateData.area2 = '';
        stateData.area3 = '';
      }

      initialProductInfoRef.current = JSON.parse(JSON.stringify(stateData));
      setProductInfo(stateData);
    }
    setRenderCount(renderCount + 1);
  }, []);

  useEffect(() => {
    productStatusRef.current = productInfo?.status.code;
  }, [productInfo]);

  const updateDisabledBtn = (value: boolean) => {
    setIsDisabledSaveBtn(value);
  };

  const handleHashChange = () => {
    const hash = window.location.hash.substring(1);
    if (hash === HASH_PRODUCT_INFO) {
      setTabNum(0);
    } else if (hash === HASH_DETAIL) {
      setTabNum(1);
    } else if (hash === HASH_PHOTO) {
      setTabNum(2);
    } else if (hash === HASH_PRICE) {
      if (isDealerType) {
        setTabNum(2);
        navigate('#photo', { replace: true });
      } else {
        setTabNum(3);
      }
    } else if (hash === HASH_DOCUMENT) {
      setTabNum(isDealerType ? 3 : 4);
    }
  };

  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [isDealerType]);

  // 증빙서류 탭 노출 여부에 따라 탭 개수가 달라지므로, 유효 범위를 벗어난 tabNum을 보정한다.
  useEffect(() => {
    const maxIndex = labelList.length - 1;
    if (tabNum > maxIndex) {
      setTabNum(maxIndex);
    }
  }, [labelList.length, tabNum]);

  return (
    <>
      <DefaultHelmet page="sale" />

      {isShowErrorPage ? (
        <NotFoundPage></NotFoundPage>
      ) : (
        <>
          <MenuHeader title="차량 정보 수정" />

          <div className="pt-[110px] w-full">
            <div className="bg-white top-[60px] w-full  max-w-[720px] fixed z-20">
              <BasicTabs labelList={labelList} value={tabNum} onChange={onChangeTab} />
            </div>
            <div className="w-full px-4 pt-[30px] pb-[90px]">
              {tabNum === 0 && (
                <CarInfo
                  productInfo={productInfo}
                  setProductInfo={setProductInfo}
                  updateDisabledBtn={updateDisabledBtn}
                  isEtc={isEtcManufacturerCategories || false}
                />
              )}
              {tabNum === 1 && (
                <DetailInfo
                  productInfo={productInfo}
                  setProductInfo={setProductInfo}
                  updateDisabledBtn={updateDisabledBtn}
                  savedAccidentContents={initialProductInfoRef.current?.accidentsHistory?.accidentContents ?? ''}
                />
              )}
              {tabNum === 2 && (
                <VehicleImageUploader
                  productInfo={productInfo}
                  setProductInfo={setProductInfo}
                  updateDisabledBtn={updateDisabledBtn}
                />
              )}
              {!isDealerType && tabNum === 3 && (
                <SalePrice
                  productInfo={productInfo}
                  setProductInfo={setProductInfo}
                  originalPrice={initialProductInfoRef.current?.price ?? undefined}
                  updateDisabledBtn={updateDisabledBtn}
                />
              )}
              {showDocumentTab && ((isDealerType && tabNum === 3) || (!isDealerType && tabNum === 4)) && (
                <EvidentiaryDocuments
                  productInfo={productInfo}
                  setProductInfo={setProductInfo}
                  updateDisabledBtn={updateDisabledBtn}
                />
              )}
            </div>
            <DualFooterButton
              leftButtonText={buttonList[0]?.title ?? '이전'}
              onClickLeftButton={buttonList[0]?.onClick ?? (() => {})}
              disabledLeft={buttonList[0]?.isDisabled ?? false}
              rightButtonText={buttonList[1]?.title ?? '저장하기'}
              onClickRightButton={
                buttonList[1]?.onClick ?? (() => handleRegistrationComplete(productInfo?.status.code, true))
              }
              disabledRight={isDisabledSaveBtn}
            />
          </div>

          <AlertModal
            isOpen={isShow}
            onClose={() => setIsShow(false)}
            content={
              <>
                개인 정보 보호를 위해 현재
                <br />
                휴대폰 번호 입력은 제한되어 있습니다.
              </>
            }
          />
          <ConfirmModal
            isOpen={isConfirmModal}
            title={isConfirmModalConfig.title}
            content={isConfirmModalConfig.content}
            leftButtonLabel={isConfirmModalConfig.leftButtonLabel}
            rightButtonLabel={isConfirmModalConfig.rightButtonLabel}
            onLeftButtonClick={isConfirmModalConfig.onLeftButtonClick}
            onRightButtonClick={isConfirmModalConfig.onRightButtonClick}
          />

          <ConfirmExitPopup
            isShow={isShowExitConfirmPopup}
            onClose={closeExitConfirmPopup}
            onConfirm={confirmExit}
            content={
              <span>
                나가시면 변경한 내용이
                <br />
                저장되지 않습니다.
              </span>
            }
          />
        </>
      )}
    </>
  );
};

export default MyCarDetailForm;
